import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class TawkService {
  public isReady = false;
  private isScriptLoaded = false;
  public isWidgetVisible = false;
  private isChatOpen = false;
  private scriptElement: HTMLScriptElement | null = null;
  private pendingLogin: { userId: string; name: string; email: string; hash: string } | null = null;
  public pendingShow = false;
  private pendingHide = false;
  private wantsVisible = false;
  private offlineSubmitCallback: ((data: { name: string; email: string; message: string }) => void) | null = null;
  private agentLeaveChatCallback: (() => void) | null = null;
  private chatMinimizedCallback: (() => void) | null = null;
  private positionInterval: ReturnType<typeof setInterval> | null = null;
  private positionRetryInterval: ReturnType<typeof setInterval> | null = null;
  private positionObserver: MutationObserver | null = null;
  private cycleAnchor: HTMLElement | null = null;
  public isPositioned: boolean = false;

  // --------------------------------
  // SCRIPT LOADING
  // --------------------------------

  /**
   * Dynamically injects the Tawk.to script into the DOM.
   * Safe to call multiple times — only loads once.
   * Must be called BEFORE setUser() / showWidget() are meaningful,
   * though those calls are queued and will execute after onLoad fires.
   */
  loadScript(): void {
    if (this.isScriptLoaded) return;
    this.isScriptLoaded = true;

    const w = window as any;
    w.Tawk_API = w.Tawk_API || {};

    // Desktop: above ordinary page content (Home's own boxes reach z-index 5, other
    // page chrome tops out around 10) but below the sidebar — its hover-expand overlay
    // reaches 9999, and even that is deliberately kept below dialogs, which start at
    // 1000 (see main.component.scss's .leftside.sidebar-over comment). A widget above
    // 9999 painted over an open sidebar, which is the one thing here meant to sit above
    // ordinary chrome. Mobile: that value would instead land ON TOP of the mobile
    // filter bottom sheets (z-index up to 71) and the drawer (999) alike, so mobile
    // stays at the widget's own low default — same 1023px breakpoint the app uses
    // elsewhere.
    const zIndex = window.innerWidth <= 1023 ? '4 !important' : '500 !important';

    w.Tawk_API.customStyle = {
      zIndex,
      visibility: {
        desktop: {
          position: 'tr',
          xOffset: '250px',  
          yOffset: -20
        },
        mobile: {
          position: 'br',
          xOffset: '-0px',
          yOffset: 0
        },
        bubble: {
          rotate: '0deg',
          xOffset: '-9999px',
          yOffset: -9999
        }
      }
    };

    // Register onLoad BEFORE the script is injected so it fires on first load.
    w.Tawk_API.onLoad = () => {
      this.isReady = true;

      if (this.pendingLogin) {
        // Only identify the visitor when agents are online/away.
        // When offline, the visitor must remain anonymous so the offline
        // contact form can submit successfully — Tawk_API.login() breaks it.
        const status: string = w.Tawk_API.getStatus();
        if (status !== 'offline') {
          if (this.pendingLogin) {
            this.loginInternal(this.pendingLogin);
          }
        }

        this.pendingLogin = null;
      }

      if (this.pendingShow) {
        this.pendingShow = false;
        // Routed back through showWidget so the not-yet-anchored case below applies
        // here too, instead of painting the bubble at tawk's default corner.
        this.showWidget();
      }

      // A hide asked for before the script was ready - session expiry bounces to
      // /login mid-load - would otherwise be lost, and the queued show above would
      // reveal the bubble on a page that has no anchor to place it on.
      if (this.pendingHide) {
        this.pendingHide = false;
        this.hideWidgetInternal();
      }
    };

    w.Tawk_API.onChatMinimized = () => {
      this.isChatOpen = false;
      if (this.chatMinimizedCallback) {
        this.chatMinimizedCallback();
      }
    };

    w.Tawk_API.onChatMaximized = () => {
      this.isChatOpen = true;
    };
    w.Tawk_API.onChatMessageAgent = () => {
      const Tawk_API = (window as any).Tawk_API;
      if (Tawk_API?.isChatMaximized && !Tawk_API.isChatMaximized()) {
        if (Tawk_API?.maximize) Tawk_API.maximize();
      }
    };

    this.scriptElement = document.createElement('script');
    this.scriptElement.src = 'https://embed.tawk.to/' + environment.tawkToWidgetKey;
    this.scriptElement.async = true;
    this.scriptElement.charset = 'UTF-8';
    this.scriptElement.setAttribute('crossorigin', '*');
    document.head.appendChild(this.scriptElement);
  }

  // --------------------------------
  // LOGIN (IDENTITY)
  // --------------------------------

  /**
   * Identifies the visitor on the Tawk dashboard.
   * Queued if the script has not finished loading yet.
   */
  setUser(userId: string, name: string, email: string, hash: string): void {
    if (this.hasTawkSession()) return;

    const payload = { userId, name, email, hash };

    if (this.isReady) {
      this.loginInternal(payload);
    } else {
      this.pendingLogin = payload;
    }
  }

  private hasTawkSession(): boolean {
    return Object.keys(localStorage).some(k => k.startsWith('twk_')) ||
      Object.keys(sessionStorage).some(k => k.startsWith('twk_'));
  }

  private loginInternal(payload: { userId: string; name: string; email: string; hash: string }): void {
    try {
      const Tawk_API = (window as any).Tawk_API;
      if (Tawk_API?.login) {
        Tawk_API.login(payload, (err: any) => {
          if (err) {
            console.error('Tawk login error:', err);
          } else {
            this.showWidget();
          }
        });
      }
    } catch (e) {
      console.error('Tawk login exception:', e);
    }
  }

  // --------------------------------
  // UI CONTROL
  // --------------------------------

  /**
   * Shows the chat widget bubble.
   * Queued if the script has not finished loading yet.
   * No-ops if the widget is already visible to prevent tawk re-rendering
   * the bubble at its default off-screen position (causing a visible blink).
   */
  showWidget(): void {
    this.pendingHide = false;
    this.wantsVisible = true;

    if (!this.isReady) {
      this.pendingShow = true;
      return;
    }

    // Held back until the bubble has been anchored into the pill. Tawk paints it at
    // its own default corner the moment it becomes visible, so showing first and
    // positioning after is what made it appear on one side and jump to the other.
    // positionBubbleAt() shows it once it lands - or once it gives up trying.
    if (!this.isPositioned) {
      this.hideWidgetInternal();
      return;
    }

    if (!this.isWidgetVisible) {
      this.showWidgetInternal();
    }
  }

  private showWidgetInternal(): void {
    try {
      const Tawk_API = (window as any).Tawk_API;
      if (Tawk_API?.showWidget) {
        Tawk_API.showWidget();
        this.isWidgetVisible = true;
      }
    } catch { }
  }

  openChat(): void {
    if (!this.isReady) {
      this.pendingShow = true;
      return;
    }

    try {
      const Tawk_API = (window as any).Tawk_API;
      if (Tawk_API?.showWidget) Tawk_API.showWidget();

      // Let Tawk handle the open transition natively. Resetting iframe styles here
      // causes the anchored bubble to disappear/jump when the pill is clicked.
      setTimeout(() => {
        if (Tawk_API?.maximize) {
          Tawk_API.maximize();
          this.isChatOpen = true;
        }
      }, 50);
    } catch { }
  }

  onOfflineSubmit(callback: (data: { name: string; email: string; message: string }) => void): void {
    this.offlineSubmitCallback = callback;
  }

  onAgentLeaveChat(callback: () => void): void {
    this.agentLeaveChatCallback = callback;
  }

  onChatMinimized(callback: (() => void) | null): void {
    this.chatMinimizedCallback = callback;
  }

  hideWidget(): void {
    this.wantsVisible = false;
    if (!this.isReady) {
      // Queued rather than dropped, and the pending show is cancelled outright:
      // whoever asked last wins, the same way it works once the script is ready.
      this.pendingShow = false;
      this.pendingHide = true;
      return;
    }
    this.hideWidgetInternal();
  }

  private hideWidgetInternal(): void {
    try {
      const Tawk_API = (window as any).Tawk_API;
      // Minimize first to close an open chat window, then hide the bubble.
      // Calling hideWidget() alone does not close a maximized chat window.
      if (Tawk_API?.minimize) Tawk_API.minimize();
      if (Tawk_API?.hideWidget) {
        Tawk_API.hideWidget();
        this.isWidgetVisible = false;
        this.isChatOpen = false;
      }
    } catch { }
  }

  positionBubbleAt(anchor: HTMLElement, size: number): void {
    // A page can host several of these widgets - web-scrapper renders three, the
    // mobile and desktop variants of the same pill - and CSS lays out only one of them.
    // Each instance calls this from its own ngOnInit, so a hidden one must not cancel
    // the cycle belonging to the one the user can actually see. It backs off whenever
    // a cycle is already running for another anchor that is still in the document -
    // "still in the document" is what separates this from a route change, where the
    // previous anchor is detached and its dead cycle SHOULD be taken over.
    if (!this.isAnchorVisible(anchor)
      && this.cycleAnchor
      && this.cycleAnchor !== anchor
      && this.cycleAnchor.isConnected) {
      return;
    }

    // Otherwise no early return on a not-yet-visible anchor. Navigating into a route
    // that recreates the topbar (onboarding -> inspirations) calls this from ngOnInit,
    // before the anchor has been laid out, and bailing here left the bubble at its
    // own default size and corner until a reload happened to get the timing right.

    // Cancel any previous positioning cycle before starting a new one.
    // Without this, each topbar recreate (hideTopbar route → listing) accumulates
    // extra polling intervals that fight each other and can cause visual glitches.
    if (this.positionInterval !== null) {
      clearInterval(this.positionInterval);
      this.positionInterval = null;
    }
    if (this.positionRetryInterval !== null) {
      clearInterval(this.positionRetryInterval);
      this.positionRetryInterval = null;
    }
    if (this.positionObserver !== null) {
      this.positionObserver.disconnect();
      this.positionObserver = null;
    }
    // A new anchor has not been positioned yet, whatever the previous one managed.
    // Leaving this true across a route change let the pill appear immediately on the
    // new page while the bubble was still sitting wherever the old page put it.
    if (this.cycleAnchor !== anchor) {
      this.isPositioned = false;
    }
    this.cycleAnchor = anchor;

    const apply = () => {
      if (!this.isAnchorVisible(anchor)) return false;


      // Tawk renders three iframes: the bubble (64x60), the chat panel (360x545) and a
      // "Visit tawk.to" badge (no width attribute at all). Only the bubble is anchored
      // into the topbar, and it is NOT the one inside .widget-visible - that container
      // holds the PANEL, so selecting by it scaled a hidden 360x545 frame and left the
      // real bubble at its own default size in the top-right corner.
      //
      // Identified by its own width attribute rather than by class, id or title: tawk
      // rewrites its classes between versions, the ids are generated per session, and
      // the two "Chat widget" titles collide and are localised. The declared width is
      // the one thing tied to what the frame actually is, and a small numeric width
      // matches the bubble alone - the panel is far wider, the badge has none.
      const candidates = (Array.from(document.querySelectorAll('iframe')) as HTMLIFrameElement[])
        .filter((frame) => {
          const width = parseFloat(frame.getAttribute('width') || '');
          return width > 0 && width <= 100;
        });
      // Prefer one that is actually rendered - when tawk swaps frames mid-session the
      // detached one keeps its attributes but measures zero.
      const bubble = candidates.find((frame) => frame.getBoundingClientRect().width > 0)
        || candidates[0];
      if (!bubble) {
        return false;
      }

      const rect = anchor.getBoundingClientRect();
      const naturalWidth = parseFloat(bubble.getAttribute('width') || '64');
      const naturalHeight = parseFloat(bubble.getAttribute('height') || '60');
      const scale = Math.min(size / naturalWidth, size / naturalHeight);

      // Position the fixed bubble over the anchor element in the pill
      bubble.style.setProperty('top', `${rect.top}px`, 'important');
      bubble.style.setProperty('left', `${rect.left}px`, 'important');
      bubble.style.setProperty('right', 'auto', 'important');
      bubble.style.setProperty('bottom', 'auto', 'important');
      bubble.style.setProperty('transform', `scale(${scale})`, 'important');
      bubble.style.setProperty('transform-origin', 'top left', 'important');
      bubble.style.setProperty('z-index', '4', 'important');

      return true;
    };

    // The anchor is measured on every tick, but the bubble is only revealed once that
    // measurement stops moving. The topbar loads its own contents asynchronously - the
    // credits pill, the profile menu, the settings icon - and each one shifts this
    // anchor sideways as it arrives, so revealing on the first successful apply only
    // moved the visible jump later instead of removing it. Two identical measurements
    // in a row is enough to call it settled.
    const anchorKey = () => {
      const rect = anchor.getBoundingClientRect();
      return `${Math.round(rect.top)}:${Math.round(rect.left)}:${Math.round(rect.width)}`;
    };

    const stopObserver = () => {
      if (this.positionObserver !== null) {
        this.positionObserver.disconnect();
        this.positionObserver = null;
      }
    };

    // The settle loop ends at the reveal, but the observer stays on through the poll
    // window below: after a login the topbar keeps filling in (credits, profile) well
    // after the anchor first settled, and each of those is a mutation.
    const stopSettling = () => {
      if (this.positionRetryInterval !== null) {
        clearInterval(this.positionRetryInterval);
        this.positionRetryInterval = null;
      }
    };

    const reveal = () => {
      this.isPositioned = true;
      if (this.wantsVisible && !this.isWidgetVisible) {
        this.showWidgetInternal();
        // Tawk re-paints the bubble as it reveals it, dropping the inline styles just
        // applied, so they go straight back on.
        apply();
      }
    };

    // Kept running after the reveal: a late layout shift (a name arriving from the
    // API) can still move the anchor once everything else has settled.
    const pollForDuration = () => {
      const end = Date.now() + 5000;
      this.positionInterval = setInterval(() => {
        apply();
        if (Date.now() >= end) {
          clearInterval(this.positionInterval!);
          this.positionInterval = null;
          stopObserver();
        }
      }, 300);
    };

    const deadline = Date.now() + 5000;
    let lastKey: string | null = null;

    const tick = () => {
      const expired = Date.now() >= deadline;

      if (!apply()) {
        lastKey = null;
        if (expired) {
          stopSettling();
          stopObserver();
          // Never anchored. Show it where tawk puts it rather than leave the user with
          // no way to reach live chat at all - misplaced beats missing.
          if (this.wantsVisible && !this.isWidgetVisible) {
            this.showWidgetInternal();
          }
        }
        return;
      }

      const key = anchorKey();
      if (key === lastKey || expired) {
        stopSettling();
        reveal();
        pollForDuration();
        return;
      }
      lastKey = key;
    };

    // The observer catches the tawk iframe arriving and keeps the styles fresh; the
    // stability check belongs to the interval alone, since two measurements taken in
    // the same mutation burst would always match and defeat it.
    this.positionObserver = new MutationObserver(() => { apply(); });
    this.positionObserver.observe(document.body, { childList: true, subtree: true });
    this.positionRetryInterval = setInterval(tick, 150);
    tick();
  }

  private isAnchorVisible(anchor: HTMLElement): boolean {
    if (!anchor?.isConnected) return false;

    const style = window.getComputedStyle(anchor);
    // if (style.display === 'none' || style.visibility === 'hidden') {
    //   return false;
    // }

    const rect = anchor.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  minimize(): void {
    if (!this.isReady) return;
    try {
      const Tawk_API = (window as any).Tawk_API;
      if (Tawk_API?.minimize) {
        Tawk_API.minimize();
        this.isChatOpen = false;
      }
    } catch { }
  }

  isChatCurrentlyOpen(): boolean {
    return this.isChatOpen;
  }

  // --------------------------------
  // LOGOUT (CRITICAL)
  // --------------------------------

  logout(): void {
    try {
      const Tawk_API = (window as any).Tawk_API;
      if (Tawk_API?.logout) {
        if (Tawk_API?.minimize) Tawk_API.minimize();
        if (Tawk_API?.hideWidget) Tawk_API.hideWidget();
        Tawk_API.logout((err: any) => {
          if (err) console.error('Tawk logout error:', err);
        });
      }
    } catch { }
    finally {
      this.clearTawkStorage();
    }

    this.removeScript();
  }

  private removeScript(): void {
    this.isPositioned = false;
    this.cycleAnchor = null;
    if (this.positionInterval !== null) {
      clearInterval(this.positionInterval);
      this.positionInterval = null;
    }
    if (this.positionRetryInterval !== null) {
      clearInterval(this.positionRetryInterval);
      this.positionRetryInterval = null;
    }
    if (this.positionObserver !== null) {
      this.positionObserver.disconnect();
      this.positionObserver = null;
    }

    this.scriptElement?.remove();
    this.scriptElement = null;

    // Remove all additional scripts Tawk.to injects at runtime (iframes, workers, etc.)
    document.querySelectorAll('script[src*="tawk.to"]').forEach(el => el.remove());

    delete (window as any).Tawk_API;
    delete (window as any).Tawk_LoadStart;

    this.isReady = false;
    this.isScriptLoaded = false;
    this.isWidgetVisible = false;
    this.isChatOpen = false;
    this.pendingLogin = null;
    this.pendingShow = false;
    this.pendingHide = false;
    this.wantsVisible = false;
    this.chatMinimizedCallback = null;
  }

  clearTawkStorage(): void {
    const isTawkKey = (k: string) => k.startsWith('twk_') || k.toLowerCase().startsWith('tawk');

    Object.keys(localStorage)
      .filter(isTawkKey)
      .forEach(k => localStorage.removeItem(k));

    Object.keys(sessionStorage)
      .filter(isTawkKey)
      .forEach(k => sessionStorage.removeItem(k));

    const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    const hostname = location.hostname;
    const parentDomain = (() => {
      try {
        const parts = new URL(environment.clientAppURL).hostname.split('.');
        return parts.length > 2 ? parts.slice(1).join('.') : null;
      } catch {
        return null;
      }
    })();

    document.cookie.split(';').forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      if (isTawkKey(name)) {
        document.cookie = `${name}=;${expired}`;                            // host-only (localhost/dev)
        document.cookie = `${name}=;${expired};domain=${hostname}`;         // exact hostname (QA on azurewebsites.net)
        if (parentDomain) {
          document.cookie = `${name}=;${expired};domain=.${parentDomain}`; // parent domain (prod: .xoopah.com)
        }
      }
    });
  }
}
